import {
  Button,
  Col,
  Input,
  Modal,
  Row,
  Spin,
  Table,
  Tag,
  Space,
  Tooltip,
  Select,
} from "antd";
import React, { useEffect, useState } from "react";
import {
  LoadingOutlined,
  EllipsisOutlined,
  EyeOutlined,
  ArrowDownOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { ColumnsType } from "antd/es/table/interface";
import dayjs from "dayjs";
import TicketServices, {
  TicketModule,
  TicketStatus,
  TicketPriority,
} from "../../Services/TicketServices";
import { PageHeader } from "../../Common/MainLayout";
import { adminContext } from "../../Common/PageRoute";
import TextArea from "antd/es/input/TextArea";
import ViewTicket from "./ViewTicket";

const { Search } = Input;

const AOTicketDashboard = () => {
  const { openNotification } = React.useContext(adminContext);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);

  const [pagingItems, setPagingItems] = useState({
    totalRecords: 0,
    currentPage: 1,
    pageSize: 10,
  });

  const [filters, setFilters] = useState({
    search: "",
    status: 0,
    priority: 0,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [viewTicketId, setViewTicketId] = useState<string | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [actionMode, setActionMode] = useState<"deescalate" | "resolve">(
    "deescalate",
  );

  const openModal = (row: any, mode: "deescalate" | "resolve") => {
    setSelectedRow(row);
    setActionMode(mode);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRow(null);
    setReason("");
    setSubmitting(false);
  };

  const handleAction = async () => {
    if (!selectedRow?.id || reason.trim().length < 5) {
      openNotification("warning", "Please enter a reason (min 5 characters).");
      return;
    }
    setSubmitting(true);
    try {
      const res =
        actionMode === "deescalate"
          ? await TicketServices.deEscalate(selectedRow.id, reason)
          : await TicketServices.resolve(selectedRow.id, reason);

      if (res?.status) {
        openNotification("success", res.message || "Action successful.");
        closeModal();
        fetchData(pagingItems.currentPage, pagingItems.pageSize);
      } else {
        openNotification("error", res?.message || "Action failed.");
      }
    } catch (error) {
      openNotification("error", "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const fetchData = async (page: number = 1, size: number = 15) => {
    setLoading(true);
    try {
      const start = (page - 1) * size;

      const res: any = await TicketServices.getList(
        start,
        size,
        filters.status,
        filters.search,
        "createdBy.date",
        "desc",
        filters.priority,
      );

      if (res) {
        setData(res.items || []);
        setPagingItems((prev: any) => ({
          ...prev,
          totalRecords: res.totalRecords || 0,
          currentPage: page,
        }));
      }
    } catch (error) {
      openNotification("error", "Failed to load tickets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1, pagingItems.pageSize);
  }, [filters.search, filters.status, filters.priority]);

  const columns: ColumnsType<any> = [
    {
      title: "S.No",
      width: 70,
      render: (_: any, __: any, i: number) =>
        (pagingItems.currentPage - 1) * pagingItems.pageSize + i + 1,
    },
    {
      title: "Reference",
      dataIndex: "number",
      width: 110,
      render: (num: any) => <a>{num}</a>,
      onCell: (row: any) => {
        return {
          onClick: () => {
            setViewTicketId(row.id);
            setIsViewOpen(true);
          },
        };
      },
    },
    {
      title: "Practice Name",
      dataIndex: "practiceName",
      width: 150,
      ellipsis: true,
      render: (value: string) => value || "-",
    },
    {
      title: "Title",
      ellipsis: true,
      dataIndex: "title",
    },
    {
      title: "Business",
      ellipsis: true,
      render: (row: any) => row?.company?.name || "-",
    },
    {
      title: "Priority",
      dataIndex: "priority",
      width: 120,
      render: (p: number) => {
        const map: any = {
          1: { color: "blue", label: "Low" },
          2: { color: "green", label: "Normal" },
          3: { color: "orange", label: "High" },
          4: { color: "red", label: "Emergency" },
        };
        return <Tag color={map[p]?.color}>{map[p]?.label || "-"}</Tag>;
      },
    },
    {
      title: "Type",
      dataIndex: "issueType",
      width: 130,
      render: (t: number) => {
        const map: any = {
          1: "Bug",
          2: "Development",
          3: "Enhancement",
        };
        return map[t] || "-";
      },
    },
    // {
    //   title: "Last Comment",
    //   render: (row: any) => {
    //     const temp = document.createElement("div");
    //     temp.innerHTML = row.comment || "";
    //     return temp.textContent || "-";
    //   },
    // },
    {
      title: "Raised By",
      ellipsis: true,
      width: 120,
      render: (row: any) => row?.createdBy?.userName || "-",
    },
    {
      title: "Created On",
      width: 120,
      render: (row: any) => dayjs(row.createdBy?.date).format("DD/MM/YYYY"),
    },
    {
      title: "Module",
      ellipsis: true,
      width: 100,
      dataIndex: "module",
      render: (m: number) => TicketModule[m] || "-",
    },
    {
      title: "Status",
      width: 100,
      render: (row: any) => {
        const map: any = {
          1: { color: "processing", text: "Open" },
          2: { color: "success", text: "Resolved" },
          3: { color: "default", text: "Closed" },
          7: { color: "blue", text: "In Progress" },
          10: { color: "red", text: "Escalated" },
        };
        const s = map[row.status] || { color: "default", text: "Unknown" };
        return <Tag color={s.color}>{s.text}</Tag>;
      },
    },
    {
      title: "Actions",
      width: 150,
      render: (row: any) => (
        <Space size="small">
          <Tooltip title="View">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => {
                setViewTicketId(row.id);
                setIsViewOpen(true);
              }}
            />
          </Tooltip>
          {row.status !== TicketStatus.Resolved &&
            row.status !== TicketStatus.Closed && (
              <Tooltip title="Mark as Resolved">
                <Button
                  type="text"
                  style={{ color: "#52c41a" }}
                  icon={<CheckCircleOutlined />}
                  onClick={() => openModal(row, "resolve")}
                />
              </Tooltip>
            )}
          {row.isEscalated && (
            <Tooltip title="De-escalate">
              <Button
                type="text"
                danger
                icon={<ArrowDownOutlined />}
                onClick={() => openModal(row, "deescalate")}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const statusOptions = [
    { label: "All Status", value: 0 },
    { label: "Open", value: TicketStatus.Open },
    { label: "Resolved", value: TicketStatus.Resolved },
    { label: "Closed", value: TicketStatus.Closed },
    { label: "Archived", value: TicketStatus.Archived },
    { label: "Deleted", value: TicketStatus.Deleted },
    { label: "UnderResearch", value: TicketStatus.UnderResearch },
    { label: "In Progress", value: TicketStatus.InProgress },
    { label: "InTesting", value: TicketStatus.InTesting },
    { label: "ReadyForRelease", value: TicketStatus.ReadyForRelease },
    { label: "Escalated", value: TicketStatus.Escalated },
    { label: "Waiting", value: TicketStatus.Waiting },
  ];

  const priorityOptions = [
    { label: "All Priority", value: 0 },
    { label: "Low", value: TicketPriority.Low },
    { label: "Normal", value: TicketPriority.Normal },
    { label: "High", value: TicketPriority.High },
    { label: "Emergency", value: TicketPriority.Emergency },
  ];

  return (
    <>
      <Row justify="start" align="middle" style={{ margin: "1px 15px" }}>
        <PageHeader item={[{ label: "Support Tickets", path: "/tickets" }]} />
      </Row>

      <div className="p-3" style={{ backgroundColor: "white" }}>
        <Row
          gutter={[12, 12]}
          className="pb-3"
          align="middle"
          justify="space-between"
        >
          {/* Left Side - Refresh */}
          <Col>
            <Button
              icon={<ReloadOutlined />}
              onClick={() =>
                fetchData(pagingItems.currentPage, pagingItems.pageSize)
              }
            >
              Refresh
            </Button>
          </Col>

          {/* Right Side - Filters */}
          <Col>
            <Row gutter={[12, 12]} align="middle">
              {/* Search */}
              <Col>
                <Search
                  style={{ width: 300 }}
                  placeholder="Search by ticket # or subject"
                  allowClear
                  onSearch={(val) =>
                    setFilters((prev) => ({
                      ...prev,
                      search: val,
                    }))
                  }
                />
              </Col>

              {/* Status Filter */}
              <Col>
                <Select
                  style={{ width: 180 }}
                  value={filters.status}
                  options={statusOptions}
                  onChange={(value) =>
                    setFilters((prev) => ({
                      ...prev,
                      status: value,
                    }))
                  }
                />
              </Col>

              {/* Priority Filter */}
              <Col>
                <Select
                  style={{ width: 150 }}
                  value={filters.priority}
                  options={priorityOptions}
                  onChange={(value) =>
                    setFilters((prev) => ({
                      ...prev,
                      priority: value,
                    }))
                  }
                />
              </Col>
            </Row>
          </Col>
        </Row>

        <Table
          rowKey="id"
          dataSource={data}
          columns={columns}
          loading={{
            spinning: loading,
            indicator: <Spin indicator={<LoadingOutlined />} />,
          }}
          pagination={{
            current: pagingItems.currentPage,
            pageSize: pagingItems.pageSize,
            total: pagingItems.totalRecords,
            onChange: (page, size) => fetchData(page, size),
          }}
        />

        <Modal
          title={
            actionMode === "deescalate"
              ? "De-escalate Ticket"
              : "Mark Ticket as Resolved"
          }
          open={isModalOpen}
          onOk={handleAction}
          onCancel={closeModal}
          okText="Confirm"
          cancelText="Cancel"
          confirmLoading={submitting}
          okButtonProps={{ disabled: reason.trim().length < 5 }}
        >
          <p style={{ marginBottom: 8 }}>
            {actionMode === "deescalate"
              ? "Provide a reason for de-escalating ticket"
              : "Provide a resolution summary for ticket"}
            <strong> #{selectedRow?.number}</strong>:
          </p>
          <TextArea
            rows={4}
            placeholder="Enter reason (min 5 characters)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoSize={{ minRows: 3, maxRows: 3 }}
          />
        </Modal>
        <ViewTicket
          id={viewTicketId}
          visible={isViewOpen}
          onClose={(refresh) => {
            setIsViewOpen(false);
            setViewTicketId(null);
            if (refresh)
              fetchData(pagingItems.currentPage, pagingItems.pageSize);
          }}
        />
      </div>
    </>
  );
};

export default AOTicketDashboard;
